import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router-dom";
import * as P from "./pages";
import RootLayout from "./layout/RootLayout";
import Home from "./pages/Home";
import PreSchool from "./pages/PreSchool";
import { PageLoader } from "./ui";
import "./global.css";

const Matriculation = lazy(() => import("./pages/Matriculation"));
const AdmissionsLanding = lazy(() => import("./pages/AdmissionsLanding"));

// Parent admissions portal
const portalAuth = () => import("./portal/AuthPages");
const ParentLoginPage = lazy(() => portalAuth().then((m) => ({ default: m.ParentLoginPage })));
const ParentSignupPage = lazy(() => portalAuth().then((m) => ({ default: m.ParentSignupPage })));
const ForgotPasswordPage = lazy(() => portalAuth().then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => portalAuth().then((m) => ({ default: m.ResetPasswordPage })));
const PortalLayout = lazy(() => import("./portal/layouts").then((m) => ({ default: m.PortalLayout })));
const DashboardPage = lazy(() => import("./portal/DashboardPage"));
const ApplyPage = lazy(() => import("./portal/ApplyPage"));
const ChildPage = lazy(() => import("./portal/ChildPage"));
const portalOther = () => import("./portal/OtherPages");
const ChildFormPage = lazy(() => portalOther().then((m) => ({ default: m.ChildFormPage })));
const AgeGuidePage = lazy(() => portalOther().then((m) => ({ default: m.AgeGuidePage })));
const AccountPage = lazy(() => portalOther().then((m) => ({ default: m.AccountPage })));

// Alumni portal
const alumni = () => import("./alumni/AlumniPages");
const AlumniLanding = lazy(() => alumni().then((m) => ({ default: m.AlumniLanding })));
const AlumniLogin = lazy(() => alumni().then((m) => ({ default: m.AlumniLogin })));
const AlumniSignup = lazy(() => alumni().then((m) => ({ default: m.AlumniSignup })));
const AlumniForgot = lazy(() => alumni().then((m) => ({ default: m.AlumniForgot })));
const AlumniReset = lazy(() => alumni().then((m) => ({ default: m.AlumniReset })));
const AlumniDashboard = lazy(() => alumni().then((m) => ({ default: m.AlumniDashboard })));

function ChildRedirect() {
  const { childId } = useParams();
  return <Navigate to={`/portal/children/${childId}`} replace />;
}

const CONTENT_ROUTES = [
  ["/Library", P.Library],
  ["/TheArts", P.TheArts],
  ["/AwardOfDiploma", P.AwardOfDiploma],
  ["/Mathematics", P.Mathematics],
  ["/Sciences", P.Sciences],
  ["/IndividualsAndSocieties", P.IndividualsAndSocieties],
  ["/LanguageAcquisition", P.LanguageAcquisition],
  ["/StudiesInLanguage", P.StudiesInLanguage],
  ["/CoreProgram", P.CoreProgram],
  ["/MYPAssessments", P.MYPAssessments],
  ["/NationalRequirements", P.NationalRequirements],
  ["/MYP", P.MYP],
  ["/hol", P.Holistic],
  ["/MYPProjects", P.MYPProjects],
  ["/ApproachesToLearn", P.ApproachesToLearn],
  ["/GlobalContexts", P.GlobalContexts],
  ["/ConceptualLearning", P.ConceptualLearning],
  ["/ExtraCircullarProgram", P.ExtraCircullarProgram],
  ["/Assessment", P.Assessment],
  ["/Curriculum", P.Curriculum],
  ["/PrimaryYearsProgram", P.PrimaryYearsProgram],
  ["/CulminatingEvents", P.CulminatingEvents],
  ["/Action", P.Action],
  ["/InternationMindedness", P.InternationMindedness],
  ["/gallery", P.Gallery],
  ["/testimonials", P.Testimonials],
  ["/OurValue", P.OurValue],
  ["/TISLanguageProfile", P.TISLanguageProfile],
  ["/TISParliament", P.TISParliament],
  ["/Admission", P.Admission],
  ["/CandidateLogin", P.CandidateLogin],
  ["/JobVacancies", P.JobVacancies],
  ["/GettingInvolved", P.GettingInvolved],
  ["/Giving", P.Giving],
  ["/ContactDetails", P.ContactDetails],
  ["/AdmissionProcess", P.AdmissionProcess],
  ["/AfricaEuropeAsia", P.AfricaEuropeAsia],
  ["/educational-technology", P.EducationalTechnology],
  ["/StudentPersonalDevelopment", P.StudentPersonalDevelopment],
  ["/CodeOfConduct", P.CodeOfConduct],
  ["/CASProgram", P.CASProgram],
  ["/SocietiesAndClub", P.SocietiesAndClub],
  ["/Cafeteria", P.Cafeteria],
  ["/Counselling", P.Counselling],
  ["/CareerAndUniCounseling", P.CareerAndUniCounseling],
  ["/IBDiploma", P.IBDiploma],
  ["/FeeStructure", P.FeeStructure],
  ["/AdmissionRequirements", P.AdmissionRequirements],
  ["/MeetTheAdmissionTeam", P.MeetTheAdmissionTeam],
  ["/INTERNATIONAL-BACCALAUREATE", P.International],
  ["/OurVission", P.OurVission],
  ["/PreSchool", PreSchool],
  ["/OrganizationalCharts", P.OrganizationalCharts],
  ["/Accreditation", P.Accreditation],
  ["/Tishistory", P.TisHistory],
  ["/StrategicPlan", P.StrategicPlan],
  ["/BoardOfGoverners", P.BoardOfGoverners],
  ["/ParentCouncil", P.ParentCouncil],
  ["/SchoolLeadershipTeam", P.SchoolLeadershipTeam],
  ["/HeadsMessage", P.HeadsMessage],
  ["/Scholarship", P.Scholarship],
];

const REDIRECTS = [
  ["/OurVision", "/OurVission"],
  ["/AlumniRegistration", "/alumni/signup"],
  ["/applynow", "/admissions"],
  ["/login", "/admissions/login"],
  ["/signup", "/admissions/signup"],
  ["/forgetpassword", "/admissions/forgot-password"],
  ["/home", "/portal"],
  ["/user", "/portal/account"],
  ["/ageChart", "/portal/age-guide"],
];

function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<RootLayout />}>
            <Route index element={<Home />} />
            {CONTENT_ROUTES.map(([path, Component]) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="/matriculation" element={<Matriculation />} />
            <Route path="/admissions" element={<AdmissionsLanding />} />
            <Route path="/alumni" element={<AlumniLanding />} />
            <Route path="/alumni/dashboard" element={<AlumniDashboard />} />
            <Route path="*" element={<P.NotFound />} />
          </Route>

          <Route path="/admissions/login" element={<ParentLoginPage />} />
          <Route path="/admissions/signup" element={<ParentSignupPage />} />
          <Route path="/admissions/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admissions/reset-password" element={<ResetPasswordPage />} />

          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="apply" element={<ApplyPage />} />
            <Route path="children/:childId" element={<ChildPage />} />
            <Route path="children/:childId/forms/:formKey" element={<ChildFormPage />} />
            <Route path="age-guide" element={<AgeGuidePage />} />
            <Route path="account" element={<AccountPage />} />
          </Route>

          <Route path="/alumni/login" element={<AlumniLogin />} />
          <Route path="/alumni/signup" element={<AlumniSignup />} />
          <Route path="/alumni/forgot-password" element={<AlumniForgot />} />
          <Route path="/alumni/reset-password" element={<AlumniReset />} />

          {REDIRECTS.map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/child-progress-admission/:childId" element={<ChildRedirect />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
